import type { CropRect, Preset, Scenario } from "./types";

export const INTERVIEW_SEGMENT_MASTER_URL = "/scenes/2k/interview-segment.master.jpg";
export const INTERVIEW_SEGMENT_MASTER_W = 2048;
export const INTERVIEW_SEGMENT_MASTER_H = 1143;

// Stage geometry (per multiview-visual-contract.md "Default Simulation Scenario"):
// the speaker's lectern is ALWAYS centered on the stage; the interviewees stand
// on audience-right; audience-left is open stage space (or holds the chairman's
// microphone during introductions). Crops below are normalized (0..1) and
// re-target the 2048x1143 2K master generated from the original 5504x3072
// Gemini 3 Pro Image master (2026-05-08).
export const INTERVIEW_SEGMENT_PRESETS = {
  // Medium Close-Up of the speaker at the centered lectern.
  speakerMcu: { x: 0.37, y: 0.20, width: 0.28, height: 0.58 },

  // Interview Mid-Shot of the two interviewees on audience-right.
  interviewMid: { x: 0.66, y: 0.17, width: 0.29, height: 0.66 },

  // Transition Shot establishing the interview geography (full stage).
  transitionShot: { x: 0.03, y: 0.08, width: 0.94, height: 0.76 },

  // For prayers: CO-160 Stage Wide.
  stageWide: { x: 0.03, y: 0.07, width: 0.94, height: 0.80 },

  // Centered lectern, tighter crop than speakerMcu.
  lecternMcu: { x: 0.40, y: 0.21, width: 0.22, height: 0.48 },
  // Centered lectern with wider context.
  lecternWide: { x: 0.32, y: 0.16, width: 0.38, height: 0.64 },
  // Wider establishing crop on the audience-right interview group.
  interviewWide: { x: 0.62, y: 0.16, width: 0.36, height: 0.68 },
} as const satisfies Record<string, CropRect>;

// Per-cam preset library for the interview-segment scenario. Display order
// is the array order; AW-RP50 numeric buttons (1..10) bind to the first 10.
// CAM 1 (audience-left of array) → audience-right of stage (interviewees).
// CAM 2 (centered) → centered lectern (speaker).
// CAM 3 (audience-right of array) → audience-left / wide / chairman's mic.
const cam1Presets: Preset[] = [
  { id: "interview-mid", label: "Interview Mid", cam: "cam1", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.interviewMid },
  { id: "interview-wide", label: "Interview Wide", cam: "cam1", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.interviewWide },
];

const cam2Presets: Preset[] = [
  { id: "speaker-mcu", label: "Speaker MCU", cam: "cam2", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.speakerMcu },
  { id: "lectern-mcu", label: "Lectern MCU", cam: "cam2", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.lecternMcu },
  { id: "lectern-wide", label: "Lectern Wide", cam: "cam2", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.lecternWide },
];

const cam3Presets: Preset[] = [
  { id: "transition-shot", label: "Transition Shot", cam: "cam3", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.transitionShot },
  { id: "stage-wide", label: "Stage Wide", cam: "cam3", scenarioId: "interview-segment", crop: INTERVIEW_SEGMENT_PRESETS.stageWide },
];

export const INTERVIEW_SEGMENT: Scenario = {
  id: "interview-segment",
  label: "Interview Segment",
  master: {
    url: INTERVIEW_SEGMENT_MASTER_URL,
    width: INTERVIEW_SEGMENT_MASTER_W,
    height: INTERVIEW_SEGMENT_MASTER_H,
  },
  presets: { cam1: cam1Presets, cam2: cam2Presets, cam3: cam3Presets },
  defaults: {
    cam1: "interview-mid",
    cam2: "speaker-mcu",
    cam3: "transition-shot",
  },
};
